import { HubFeature } from '@ats/api/cm';
import { LocDefs, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Fulfillment Vouchers|Upload';
const instructionHTML = `
<p>Follow the instructions below to format a CSV file that will allow you to import Fulfillment Voucher tokens in bulk.</p>
<p>Select the CSV file, then press the "Upload" button. Include the following column headers will be accepted in the CSV file.</p>
<ul>
	<li>VoucherID</li>
</ul>
<p>Omitting the required columns may result in an error. Extra fields will be ignored.</p>
<p>Note: There is a 500 row limit.</p>
`;

export type SPFulfillmentVoucherUploadFormLocs =
	| 'SPFulfillmentVoucherUploadFormSectionTitle'
	| 'SPFulfillmentVoucherUploadHideInstructions'
	| 'SPFulfillmentVoucherUploadShowInstructions'
	| 'SPFulfillmentVoucherUploadInstructions'
	| 'SPFulfillmentVoucherUploadBatchIdLabel'
	| 'SPFulfillmentVoucherUploadBatchIdRequired'
	| 'SPFulfillmentVoucherUploadExpirationDateLabel'
	| 'SPFulfillmentVoucherUploadSubmitButton'
	| 'SPFulfillmentVoucherUploadSuccess'
	| 'fe02dd74111d41cabd7724e14347e7fe'
	| 'a6679e52c6b94a2693d3d47fbe7205a9'
	| 'fed04948c41640ceb63120ca773a0932';

export const spFulfillmentVoucherUploadFormLocs: LocDefs<SPFulfillmentVoucherUploadFormLocs> = {
	SPFulfillmentVoucherUploadFormSectionTitle: {
		defaultValue: 'Upload Fulfillment Vouchers Token File',
		description: 'Title displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Section Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadHideInstructions: {
		defaultValue: 'Hide Instructions',
		description: 'Hide Instructions displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Hide Instructions`,
		displayType: 'Button',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadShowInstructions: {
		defaultValue: 'Show Instructions',
		description: 'Show Instructions displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Show Instructions`,
		displayType: 'Button',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadInstructions: {
		defaultValue: instructionHTML,
		description: 'Upload Instructions displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Upload Instructions`,
		displayType: 'Label',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: true,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadBatchIdLabel: {
		defaultValue: 'Batch ID',
		description: 'Batch ID displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Upload Batch ID Label`,
		displayType: 'Label',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadBatchIdRequired: {
		defaultValue: 'Batch ID is required',
		description: 'Batch ID required displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Upload Batch ID Required`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadExpirationDateLabel: {
		defaultValue: 'Expiration Date',
		description: 'Expiration Date displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Upload Expiration Date Label`,
		displayType: 'Label',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadSubmitButton: {
		defaultValue: 'Upload',
		description: 'Upload button displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Upload Submit Button`,
		displayType: 'Button',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	SPFulfillmentVoucherUploadSuccess: {
		defaultValue: 'Fulfillment Vouchers uploaded successfully',
		description: 'Success message displayed in the upload fulfillment vouchers section.',
		displayPath: `${dpRoot}|Upload Success`,
		displayType: 'Notification',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	fe02dd74111d41cabd7724e14347e7fe: {
		defaultValue: 'Voucher IDs already exist',
		description: 'Error message displayed when the voucher IDs already exist.',
		displayPath: `${dpRoot}|Voucher IDs Already Exist`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	a6679e52c6b94a2693d3d47fbe7205a9: {
		defaultValue: 'Duplicate voucher IDs found',
		description: 'Error message displayed when the duplicate voucher IDs are found.',
		displayPath: `${dpRoot}|Duplicate Voucher IDs Found`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	fed04948c41640ceb63120ca773a0932: {
		defaultValue: 'Voucher numbers already exist',
		description: 'Error message displayed when the voucher numbers already exist.',
		displayPath: `${dpRoot}|Voucher Numbers Already Exist`,
		displayType: 'Error',
		feature: HubFeature.CredentialFulfillmentFulfillmentVouchers,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
};

export const spFulfillmentVoucherUploadFormLocKeys = MapLocKeys<SPFulfillmentVoucherUploadFormLocs>(
	spFulfillmentVoucherUploadFormLocs
);

export type SPFulfillmentVoucherUploadFormLocKeys = typeof spFulfillmentVoucherUploadFormLocKeys;
